import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  EditorContent,
  EditorContext,
  useEditor,
  type Editor,
  type Range,
} from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { Zap } from 'lucide-react';
import { cn } from '@cotera/client/v0/lib/utils';
import { VariablePill } from './variable-pill.node';
import { editorToTemplate, templateToDoc } from './token-serialization';
import { VariableSuggestionMenu } from './variable-suggestion-menu';
import { TokenSourceLabelsContext } from './token-source-labels';
import {
  sourceLabelsFromGroups,
  type UpstreamFieldOption,
  type UpstreamSourceGroup,
} from '../variable-picker-data';

// A plain single-/multi-line text surface: keep the base doc/paragraph/text +
// history, drop every block and mark formatting so this reads as an input, not
// a document editor.
const STARTER_KIT = StarterKit.configure({
  bold: false,
  italic: false,
  strike: false,
  code: false,
  underline: false,
  link: false,
  heading: false,
  blockquote: false,
  bulletList: false,
  orderedList: false,
  listItem: false,
  listKeymap: false,
  codeBlock: false,
  horizontalRule: false,
  trailingNode: false,
});

export type TokenInputProps = {
  value: string;
  multiline?: boolean;
  placeholder?: string;
  disabled?: boolean;
  sources: readonly UpstreamSourceGroup[];
  onChange: (template: string) => void;
  insertVariable?: (
    editor: Editor,
    field: UpstreamFieldOption,
    range?: Range
  ) => void;
};

function labelForField(field: UpstreamFieldOption): string | undefined {
  return field.depth === 0 && field.leaf === field.label
    ? field.label
    : undefined;
}

function insertVariablePill(
  editor: Editor,
  field: UpstreamFieldOption,
  range?: Range
): void {
  const chain = editor.chain().focus();
  if (range !== undefined && range.from < range.to) {
    chain.deleteRange(range);
  }
  chain
    .insertVariablePill({
      ref: field.ref,
      label: labelForField(field),
    })
    .run();
}

/**
 * A token/template text input: static text mixed with atomic variable pills that
 * reference previous-step outputs. Serializes to a `{{ref}}` template string.
 * Type `/` (or click the bolt) to insert a variable.
 */
export const TokenInput: React.FC<TokenInputProps> = ({
  value,
  multiline = false,
  placeholder,
  disabled = false,
  sources,
  onChange,
  insertVariable = insertVariablePill,
}) => {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Tracks the last value we pushed via onUpdate so a stale controlled `value`
  // (e.g. intermediate '' from a non-atomic replace) cannot clobber newer
  // editor content and leave orphaned React node views.
  const lastEmittedRef = useRef(value);

  const [isEmpty, setIsEmpty] = useState(() => value === '');

  const editor = useEditor({
    extensions: [STARTER_KIT, VariablePill],
    content: templateToDoc(value),
    editable: !disabled,
    editorProps: {
      attributes: {
        class: cn(
          // `break-words` so a long unbroken literal (or pasted ref) wraps
          // instead of overflowing the narrow inspector and getting clipped.
          'outline-none text-sm px-3 py-1.5 [overflow-wrap:anywhere]',
          multiline ? 'min-h-[72px]' : 'min-h-8'
        ),
      },
      handleKeyDown: (_view, event) => {
        // Single-line fields never break to a new line.
        if (!multiline && event.key === 'Enter') {
          event.preventDefault();
          return true;
        }
        return false;
      },
    },
    onCreate: ({ editor }) => {
      const current = editorToTemplate(editor);
      lastEmittedRef.current = current;
      setIsEmpty(current === '');
    },
    onUpdate: ({ editor }) => {
      const nextValue = editorToTemplate(editor);
      lastEmittedRef.current = nextValue;
      setIsEmpty(nextValue === '');
      onChangeRef.current(nextValue);
    },
  });

  useEffect(() => {
    if (editor === null || editor.isDestroyed) {
      return;
    }

    if (value === lastEmittedRef.current) {
      setIsEmpty(value === '');
      return;
    }

    const current = editorToTemplate(editor);
    // Parent is behind an in-flight emit (still '' while we already inserted).
    // Applying it would wipe the pill and can leave a ghost NodeView.
    if (value === '' && current !== '' && current === lastEmittedRef.current) {
      return;
    }

    if (current !== value) {
      editor.commands.setContent(templateToDoc(value), { emitUpdate: false });
    }
    lastEmittedRef.current = value;
    setIsEmpty(value === '');
  }, [editor, value]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  // `SuggestionMenu` + its keyboard navigation resolve the editor from context
  // (matching the app's `@`/`/` menus). Provide it rather than passing a prop so
  // the capture-phase key handler binds to this editor's DOM.
  const contextValue = useMemo(() => ({ editor }), [editor]);

  // Pills are rebuilt from the saved `{{ref}}`, which carries a source id and no
  // name, so the names have to reach them from here — see
  // `TokenSourceLabelsContext`.
  const sourceLabels = useMemo(
    () => sourceLabelsFromGroups(sources),
    [sources]
  );

  return (
    <TokenSourceLabelsContext.Provider value={sourceLabels}>
      <EditorContext.Provider value={contextValue}>
        <div
          className={cn(
            'border-input focus-within:border-ring focus-within:ring-ring/50 relative flex min-h-8 items-start rounded-md border bg-transparent shadow-xs transition-[color,box-shadow] focus-within:ring-[3px]',
            disabled && 'pointer-events-none opacity-50'
          )}
        >
          <div className="relative min-w-0 flex-1">
            {isEmpty && placeholder !== undefined && placeholder !== '' ? (
              <span className="text-muted-foreground pointer-events-none absolute left-3 right-2 top-1.5 block truncate text-sm">
                {placeholder}
              </span>
            ) : null}
            <EditorContent editor={editor} />
          </div>
          <button
            type="button"
            title="Insert data from a previous step"
            disabled={disabled || sources.length === 0}
            onClick={() => editor?.chain().focus().insertContent('/').run()}
            className="text-muted-foreground hover:text-foreground hover:bg-muted/60 m-1 flex size-6 shrink-0 items-center justify-center rounded disabled:opacity-40"
          >
            <Zap className="size-4" />
          </button>
          {editor !== null ? (
            <VariableSuggestionMenu
              sources={sources}
              onInsert={(ed, field, range) => insertVariable(ed, field, range)}
            />
          ) : null}
        </div>
      </EditorContext.Provider>
    </TokenSourceLabelsContext.Provider>
  );
};
